import { Upload, Bot, MessageSquare, Heart, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const HowItWorks = () => {
  const { t } = useTranslation();
  
  const steps = [
    { 
      icon: Upload, 
      title: t('howItWorks.step1'), 
      description: "Complete your profile with photos and interests",
      color: 'from-primary to-primary-light',
      number: '01'
    },
    { 
      icon: Bot, 
      title: t('howItWorks.step2'), 
      description: "Our AI learns your preferences and personality",
      color: 'from-accent to-accent',
      number: '02'
    },
    { 
      icon: MessageSquare, 
      title: t('howItWorks.step3'), 
      description: "Review AI-generated compatibility insights",
      color: 'from-secondary to-secondary-light',
      number: '03'
    },
    { 
      icon: Heart, 
      title: t('howItWorks.step4'), 
      description: "Connect with your most compatible matches",
      color: 'from-primary to-accent',
      number: '04'
    }
  ];

  return (
    <section id="how-it-works" className="py-24 sm:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-muted/30" />
      <div className="absolute inset-0 bg-gradient-mesh opacity-30" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20 stagger-children">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 mb-6">
            <span className="text-sm font-medium text-secondary">How It Works</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            Your Journey to Love
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Your journey to meaningful connections starts with four simple steps
          </p>
        </div>

        {/* Steps - Desktop */}
        <div className="hidden lg:block max-w-6xl mx-auto mb-16">
          <div className="relative">
            {/* Connection line */}
            <div className="absolute top-24 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-primary via-accent to-secondary opacity-30" />
            
            <div className="grid grid-cols-4 gap-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="relative group">
                    {/* Step card */}
                    <div className="glass-card p-8 rounded-3xl text-center h-full">
                      {/* Number badge */}
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-bold bg-gradient-to-r ${step.color} text-primary-foreground shadow-soft`}>
                          {step.number}
                        </span>
                      </div>
                      
                      {/* Icon */}
                      <div className="mb-6 flex justify-center pt-4">
                        <div className={`relative w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-soft group-hover:shadow-glow transition-all duration-500 group-hover:scale-110`}>
                          <Icon className="w-10 h-10 text-primary-foreground" />
                        </div>
                      </div>
                      
                      {/* Content */}
                      <h3 className="text-lg font-bold text-foreground mb-3">
                        {step.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                    
                    {/* Arrow */}
                    {index < steps.length - 1 && (
                      <div className="absolute top-24 -right-4 z-10 hidden lg:block">
                        <ArrowRight className="w-6 h-6 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Steps - Mobile/Tablet */}
        <div className="lg:hidden space-y-6 max-w-md mx-auto">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={index} className="glass-card p-6 rounded-2xl">
                <div className="flex items-start gap-5">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-soft`}>
                    <Icon className="w-8 h-8 text-primary-foreground" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-gradient-to-r ${step.color} text-primary-foreground`}>
                        {step.number}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-1">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
